import type { LiteClient } from 'algoliasearch/lite'

/**
 * Algolia Secured API Key shared by every connected search surface.
 *
 * Mechanism ported from front-b2ag (`@b2ag/search`), but kept **framework- and
 * config-source-agnostic**: this package depends neither on Vue nor on
 * `window.env`/`getConfig`. The consumer injects, at bootstrap via
 * {@link configureAccessKey}, the key provider (product BFF call authenticated by the
 * user JWT) and the public fallback key.
 *
 * State (cached key, refresh timer, registered clients) lives in module singletons:
 * a single key for the whole session, shared across the store, the search view and
 * the product micro-front.
 */
export type AlgoliaAccessKey = {
  securedApiKey: string
  indexName: string
  /** Unix expiry timestamp, in **seconds** (as returned by the BFF). */
  validUntil: number
}

export type FetchAccessKeyParams = {
  distributorId: string
  /** Targeted member's membership number — unused for a seller (catalog_manager). */
  beneficiaryId?: string
  locale?: string
}

/** Fetches a Secured API Key from the product BFF (injected at bootstrap). */
export type SecuredKeyProvider = (params: FetchAccessKeyParams) => Promise<AlgoliaAccessKey>

export type AccessKeyConfig = {
  securedKeyProvider: SecuredKeyProvider
  /** Search-only public key, applied as a fallback and after a purge. */
  publicApiKey: string
  /** Optional logging of fetch failures. */
  onError?: (error: unknown) => void
}

/** Margin before `validUntil` under which the key is considered "to refresh". */
const REFRESH_MARGIN_MS = 5 * 60 * 1000

// Module singletons — see the header comment.
let config: AccessKeyConfig | null = null
let cached: AlgoliaAccessKey | null = null
let cachedDiscriminant: string | null = null
let lastParams: FetchAccessKeyParams | null = null
let pendingFetch: Promise<AlgoliaAccessKey | null> | null = null
let pendingDiscriminant: string | null = null
let refreshTimer: ReturnType<typeof setTimeout> | null = null
// Long-lived search-only clients whose key must be rotated in place.
const registeredClients = new Set<LiteClient>()

export function configureAccessKey(nextConfig: AccessKeyConfig): void {
  config = nextConfig
}

function discriminant(params: FetchAccessKeyParams): string {
  return `${params.distributorId}|${params.beneficiaryId ?? ''}|${params.locale ?? ''}`
}

function isFresh(): boolean {
  return cached !== null && cached.validUntil * 1000 - Date.now() > REFRESH_MARGIN_MS
}

/** Cached secured key while still fresh, otherwise `null`. */
export function getCachedAccessKey(): AlgoliaAccessKey | null {
  return isFresh() ? cached : null
}

/** v3 index name carried by the secured key when available, otherwise the given v2 index. */
export function resolveProductIndexName(fallbackV2IndexName: string): string {
  return getCachedAccessKey()?.indexName ?? fallbackV2IndexName
}

function rotateRegisteredClients(apiKey: string): void {
  registeredClients.forEach((client) => client.setClientApiKey({ apiKey }))
}

function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
}

/** Falls back to the public key: purges the cache and puts clients back on the static key. */
function downgradeToPublicKey(): void {
  cached = null
  cachedDiscriminant = null
  clearRefreshTimer()
  if (config) rotateRegisteredClients(config.publicApiKey)
}

function scheduleProactiveRefresh(key: AlgoliaAccessKey): void {
  clearRefreshTimer()
  const delay = Math.max(0, key.validUntil * 1000 - Date.now() - REFRESH_MARGIN_MS)
  refreshTimer = setTimeout(() => {
    if (!lastParams) return
    fetchAccessKey(lastParams)
      .then((refreshed) => {
        if (!refreshed) downgradeToPublicKey()
      })
      .catch(() => {})
  }, delay)
}

/**
 * Fetches (and caches) the distributor's Secured API Key. Deduplicates concurrent
 * calls for the same context, rotates the registered clients onto the obtained key
 * and schedules a proactive refresh before expiry. Returns `null` if the fetch fails
 * (clients then stay on the public key).
 */
export async function fetchAccessKey(params: FetchAccessKeyParams): Promise<AlgoliaAccessKey | null> {
  if (!config) {
    throw new Error('access-key: configureAccessKey must be called before fetchAccessKey')
  }

  const nextDiscriminant = discriminant(params)
  const paramsChanged = nextDiscriminant !== cachedDiscriminant
  lastParams = params

  if (!paramsChanged && isFresh()) return cached
  if (pendingFetch && pendingDiscriminant === nextDiscriminant) return pendingFetch
  // Different context: fall back to the public key until the new key is obtained.
  if (paramsChanged && cached) downgradeToPublicKey()

  const { securedKeyProvider, onError } = config
  const fetchPromise: Promise<AlgoliaAccessKey | null> = securedKeyProvider(params)
    .then((response) => {
      // Context changed during the request: drop the now-stale result.
      if (!lastParams || discriminant(lastParams) !== nextDiscriminant) return null
      const key: AlgoliaAccessKey = {
        securedApiKey: response.securedApiKey,
        indexName: response.indexName,
        validUntil: response.validUntil,
      }
      cached = key
      cachedDiscriminant = nextDiscriminant
      rotateRegisteredClients(key.securedApiKey)
      scheduleProactiveRefresh(key)
      return key
    })
    .catch((error) => {
      onError?.(error)
      return null
    })
    .finally(() => {
      if (pendingFetch === fetchPromise) {
        pendingFetch = null
        pendingDiscriminant = null
      }
    })

  pendingFetch = fetchPromise
  pendingDiscriminant = nextDiscriminant
  return fetchPromise
}

/**
 * Registers a long-lived search-only client so its key is rotated in place when the
 * Secured API Key arrives / is refreshed / is purged. Immediately applies the current
 * secured key if one is already cached.
 */
export function registerSecuredClient(client: LiteClient): LiteClient {
  registeredClients.add(client)
  const current = getCachedAccessKey()
  if (current) client.setClientApiKey({ apiKey: current.securedApiKey })
  return client
}

export function unregisterSecuredClient(client: LiteClient): void {
  registeredClients.delete(client)
}

/** Resets the state (logout, switch to anonymous, tests): purges the key and falls back to the public key. */
export function clearAccessKey(): void {
  downgradeToPublicKey()
  lastParams = null
  pendingFetch = null
  pendingDiscriminant = null
  // The client registry is intentionally kept (session singletons).
}
